import { GameState } from '../domain/entities';
import { GAME_VERSION_NUMBER } from '../version';
import { mensGameConfig } from '../data/mens/config';
import { womensGameConfig } from '../data/womens/config';

const compareVersions = (v1: string, v2: string) => {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    const len = Math.max(parts1.length, parts2.length);
    for (let i = 0; i < len; i++) {
        const p1 = parts1[i] || 0;
        const p2 = parts2[i] || 0;
        if (p1 > p2) return 1;
        if (p1 < p2) return -1;
    }
    return 0;
};

const migrations: Record<string, (state: any) => any> = {
    '6.9.16': (state: any): any => {
        console.log('Running migration from 6.9.16 to 6.10.0');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                if (typeof team.managerInfluence === 'number') {
                    team.boardConfidence = team.managerInfluence;
                    delete team.managerInfluence;
                } else if (typeof team.boardConfidence === 'undefined') {
                    team.boardConfidence = 50; // Set a default if neither exists
                }
            }
        }
        return state;
    },
    '6.10.0': (state: any): any => {
        console.log('Running migration to add debugFlags for version 6.11.0');
        if (!state.debugFlags) {
            state.debugFlags = {
                adsDisabled: false,
                moneyAdded: false,
                simulationUsed: false,
                aiPlayerUsed: false,
            };
        } else if (typeof state.debugFlags.aiPlayerUsed === 'undefined') {
            state.debugFlags.aiPlayerUsed = false;
        }
        return state;
    },
    '6.12.1': (state: any): any => {
        console.log('Running migration to set managerName for version 7.0.0');
        if (typeof state.managerName === 'undefined') {
            state.managerName = 'Manager';
        }
        // Add retiredLegends to clubHistory
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                if (team.clubHistory && !team.clubHistory.retiredLegends) {
                    team.clubHistory.retiredLegends = [];
                }
            }
        }
        return state;
    },
    '8.0.0': (state: any): any => {
        console.log('Running migration for version 8.0.0');
        if (typeof state.transferBanned === 'undefined') {
            state.transferBanned = false;
        }
         for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                for (const player of team.squad) {
                    if (typeof player.wantsToTalk === 'undefined') {
                        player.wantsToTalk = false;
                    }
                }
            }
        }
        return state;
    },
    '8.0.1': (state: any): any => {
        console.log('Running migration for version 8.0.1: Adding morale reasons');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                for (const player of team.squad) {
                    if (typeof player.moraleReason === 'undefined') {
                        // Set a default reason based on current morale
                        switch (player.morale) {
                            case 'Unhappy':
                            case 'Furious':
                                player.moraleReason = 'General unhappiness';
                                break;
                            case 'Ecstatic':
                            case 'Happy':
                                player.moraleReason = 'Good form';
                                break;
                            default:
                                player.moraleReason = 'Feeling content';
                        }
                    }
                }
                if (team.youthAcademy && team.youthAcademy.squad) {
                    for (const player of team.youthAcademy.squad) {
                         if (typeof player.moraleReason === 'undefined') {
                            player.moraleReason = 'Feeling content';
                         }
                    }
                }
            }
        }
        return state;
    },
    '8.1.0': (state: any): any => {
        console.log('Running migration for version 8.1.0: Adding consecutiveBadFormGames');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                for (const player of team.squad) {
                    if (typeof player.consecutiveBadFormGames === 'undefined') {
                        player.consecutiveBadFormGames = 0;
                    }
                }
                if (team.youthAcademy && team.youthAcademy.squad) {
                    for (const player of team.youthAcademy.squad) {
                         if (typeof player.consecutiveBadFormGames === 'undefined') {
                            player.consecutiveBadFormGames = 0;
                         }
                    }
                }
            }
        }
        return state;
    },
    '8.6.0': (state: any): any => {
        console.log('Running migration for version 8.7.0: Adding new tactical options');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                if (team.tactics) {
                    if (typeof team.tactics.attackingFocus === 'undefined') {
                        team.tactics.attackingFocus = 'Mixed';
                    }
                    if (typeof team.tactics.passingStyle === 'undefined') {
                        team.tactics.passingStyle = 'Mixed';
                    }
                    if (typeof team.tactics.defensiveStyle === 'undefined') {
                        team.tactics.defensiveStyle = 'Standard';
                    }
                    if (typeof team.tactics.tacklingStyle === 'undefined') {
                        team.tactics.tacklingStyle = 'Normal';
                    }
                }
            }
        }
        return state;
    },
    '8.7.0': (state: any): any => {
        console.log('Running migration for version 8.7.0: Ensuring config object exists');
        if (!state.config) {
            console.log('State is missing config object. Adding it now.');
            state.config = state.gameType === 'womens' ? womensGameConfig : mensGameConfig;
        }
        return state;
    },
    '10.6.0': (state: any): any => {
        console.log('Running migration for version 10.7.0: Adding alwaysWin debug flag');
        if (state.debugFlags) {
            if (typeof state.debugFlags.alwaysWin === 'undefined') {
                state.debugFlags.alwaysWin = false;
            }
        }
        return state;
    },
    '10.9.0': (state: any): any => {
        console.log('Running migration for version 11.0.0: Adding fitness attributes');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                const processPlayer = (player: any) => {
                    if (typeof player.fitness === 'undefined') {
                        player.fitness = 100;
                        player.fitnessMax = 80 + Math.floor(Math.random() * 21); // 80-100
                        player.fitnessRecovery = 8 + Math.floor(Math.random() * 9); // 8-16
                    }
                };
                team.squad.forEach(processPlayer);
                if (team.youthAcademy && team.youthAcademy.squad) {
                    team.youthAcademy.squad.forEach(processPlayer);
                }
            }
        }
        return state;
    },
    '11.3.0': (state: any): any => {
        console.log('Running migration for version 11.4.0: Adding showHiddenInfo debug flag');
        if (state.debugFlags) {
            if (typeof state.debugFlags.showHiddenInfo === 'undefined') {
                state.debugFlags.showHiddenInfo = false;
            }
        }
        return state;
    },
    '11.9.0': (state: any): any => {
        console.log('Running migration for version 11.10.0: Adding favored foot and position');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                const processPlayer = (player: any) => {
                    if (typeof player.favoredFoot === 'undefined') {
                        const rand = Math.random();
                        if (rand < 0.65) player.favoredFoot = 'Right';
                        else if (rand < 0.9) player.favoredFoot = 'Left';
                        else player.favoredFoot = 'Either';
                    }
                    if (typeof player.favoredPositionCategory === 'undefined') {
                        switch (player.position) {
                            case 'GK': player.favoredPositionCategory = 'Central'; break;
                            case 'DEF': player.favoredPositionCategory = Math.random() < 0.5 ? 'Central' : 'Wide'; break;
                            case 'MID': player.favoredPositionCategory = Math.random() < 0.5 ? 'Central' : 'Wide'; break;
                            case 'FWD': player.favoredPositionCategory = Math.random() < 0.6 ? 'Central' : 'Wide'; break;
                            default: player.favoredPositionCategory = 'Any';
                        }
                    }
                };
                team.squad.forEach(processPlayer);
                if (team.youthAcademy && team.youthAcademy.squad) {
                    team.youthAcademy.squad.forEach(processPlayer);
                }
            }
        }
        return state;
    },
    '11.10.1': (state: any): any => {
        console.log('Running migration for version 11.11.0: Adding training facilities and focuses');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                if (typeof team.trainingFacilities === 'undefined') {
                    team.trainingFacilities = { level: 1 };
                }
                if (typeof team.positionalTrainingFocus === 'undefined') {
                    team.positionalTrainingFocus = { GK: null, DEF: null, MID: null, FWD: null };
                }
                // Migrate old team training focus values
                if (team.trainingFocus === 'Defensive') {
                    team.trainingFocus = 'Defending';
                }
                // Attacking, Fitness, Rest are fine as they are.

                // Migrate old individual training focus values
                const processPlayer = (player: any) => {
                    if (player.trainingFocus === 'Defensive') {
                        player.trainingFocus = 'Defending';
                    }
                };
                team.squad.forEach(processPlayer);
                if (team.youthAcademy && team.youthAcademy.squad) {
                    team.youthAcademy.squad.forEach(processPlayer);
                }
            }
        }
        return state;
    },
    '11.20.0': (state: any): any => {
        console.log('Running migration for version 11.21.0: Adding expectedWage');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                const processPlayer = (player: any) => {
                    if (typeof player.expectedWage === 'undefined') {
                        player.expectedWage = player.wage || 0;
                    }
                };
                team.squad.forEach(processPlayer);
                if (team.youthAcademy && team.youthAcademy.squad) {
                    team.youthAcademy.squad.forEach(processPlayer);
                }
            }
        }
        return state;
    },
    '11.28.2': (state: any): any => {
        console.log('Running migration for version 11.28.3: Adding injury history');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                const processPlayer = (player: any) => {
                    if (typeof player.injuryHistory === 'undefined') {
                        player.injuryHistory = [];
                        // If player is currently injured, add it to their history
                        if (player.injury) {
                            player.injuryHistory.push({
                                season: state.season,
                                week: state.currentWeek,
                                type: player.injury.type,
                                duration: player.injury.duration
                            });
                        }
                    }
                };
                team.squad.forEach(processPlayer);
                if (team.youthAcademy && team.youthAcademy.squad) {
                    team.youthAcademy.squad.forEach(processPlayer);
                }
            }
        }
        return state;
    },
    '11.29.18': (state: any): any => {
        console.log('Running migration for version 11.30.0: Adding yellow card accumulation');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                const processPlayer = (player: any) => {
                    if (typeof player.yellowCardAccumulation === 'undefined') {
                        // Set to current yellow cards as initial value
                        player.yellowCardAccumulation = player.yellowCards || 0;
                    }
                };
                team.squad.forEach(processPlayer);
                if (team.youthAcademy && team.youthAcademy.squad) {
                    team.youthAcademy.squad.forEach(processPlayer);
                }
            }
        }
        return state;
    },
    '11.47.0': (state: any): any => {
        console.log('Running migration for version 11.48.0: Adding assistant manager');
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                if (typeof team.assistantManager === 'undefined') {
                    team.assistantManager = {
                        level: 1,
                        transferSuggestions: []
                    };
                }
            }
        }
        return state;
    },
    '13.16.0': (state: any): any => {
        console.log('Running migration for version 13.16.0: Adding free agents and search history');
        // Ensure freeAgents array exists (added in v13.16.0)
        if (!state.freeAgents) {
            state.freeAgents = [];
        }
        // Ensure searchHistory array exists (added in v13.16.0)
        if (!state.searchHistory) {
            state.searchHistory = [];
        }
        return state;
    },
    '13.16.9': (state: any): any => {
        console.log('Running migration for version 13.16.9: Adding hue and faceEmoji to free agents');
        // Ensure free agents have hue and faceEmoji properties for rendering
        if (state.freeAgents && Array.isArray(state.freeAgents)) {
            state.freeAgents = state.freeAgents.map((agent: any) => {
                if (typeof agent.hue === 'undefined') {
                    agent.hue = Math.floor(Math.random() * 360);
                }
                if (typeof agent.faceEmoji === 'undefined') {
                    agent.faceEmoji = '😊'; // Default face if missing
                }
                return agent;
            });
        }
        return state;
    },
    '13.16.17': (state: any): any => {
        console.log('Running migration for version 13.16.17: Redesigning free agent system with 2-week decision window');

        // Initialize new free agent fields
        if (state.freeAgents && Array.isArray(state.freeAgents)) {
            state.freeAgents = state.freeAgents.map((agent: any) => {
                // Initialize bid deadline (2 weeks from now)
                if (!agent.bidDeadline) {
                    agent.bidDeadline = {
                        season: state.season,
                        week: Math.min(state.currentWeek + 2, state.config?.WEEKS_PER_SEASON || 52)
                    };
                }
                // Count existing offers as initial bids
                if (!agent.totalBids && agent.offers) {
                    agent.totalBids = agent.offers.length;
                } else if (!agent.totalBids) {
                    agent.totalBids = 0;
                }
                // Initialize user bid status
                if (!agent.userBidStatus) {
                    agent.userBidStatus = 'no_bid';
                }
                return agent;
            });
        }

        return state;
    },
    '13.16.11': (state: any): any => {
        console.log('Running migration for version 13.16.11: Adding playerSnapshot to free agents and flexibility to all players');

        // Ensure free agents have playerSnapshot for detail view
        if (state.freeAgents && Array.isArray(state.freeAgents)) {
            state.freeAgents = state.freeAgents.map((agent: any) => {
                if (typeof agent.playerSnapshot === 'undefined') {
                    agent.playerSnapshot = undefined;
                }
                return agent;
            });
        }

        // Initialize flexibility and favoredFoot on all players in all leagues
        if (state.leagues) {
            Object.values(state.leagues).forEach((league: any) => {
                if (league.teams && Array.isArray(league.teams)) {
                    league.teams.forEach((team: any) => {
                        if (team.squad && Array.isArray(team.squad)) {
                            team.squad.forEach((player: any) => {
                                if (!player.flexibility || typeof player.flexibility !== 'object') {
                                    player.flexibility = {
                                        categoryFlexibility: 70,
                                        footFlexibility: 70
                                    };
                                }
                                if (!player.favoredFoot) {
                                    player.favoredFoot = 'Either'; // Default to both feet
                                }
                                if (!player.favoredPositionCategory) {
                                    player.favoredPositionCategory = 'Any'; // Default to flexible positioning
                                }
                                if (!player.attributes || typeof player.attributes !== 'object') {
                                    player.attributes = {}; // Initialize empty attributes if missing
                                }
                            });
                        }
                    });
                }
            });
        }

        // Initialize flexibility and favoredFoot on retired players
        if (state.retiredPlayers && Array.isArray(state.retiredPlayers)) {
            state.retiredPlayers.forEach((player: any) => {
                if (!player.flexibility || typeof player.flexibility !== 'object') {
                    player.flexibility = {
                        categoryFlexibility: 70,
                        footFlexibility: 70
                    };
                }
                if (!player.favoredFoot) {
                    player.favoredFoot = 'Either'; // Default to both feet
                }
                if (!player.favoredPositionCategory) {
                    player.favoredPositionCategory = 'Any'; // Default to flexible positioning
                }
                if (!player.attributes || typeof player.attributes !== 'object') {
                    player.attributes = {}; // Initialize empty attributes if missing
                }
            });
        }

        // Initialize flexibility, favoredFoot, favoredPositionCategory, and attributes on free agents playerSnapshot
        if (state.freeAgents && Array.isArray(state.freeAgents)) {
            state.freeAgents.forEach((agent: any) => {
                if (agent.playerSnapshot) {
                    if (!agent.playerSnapshot.flexibility || typeof agent.playerSnapshot.flexibility !== 'object') {
                        agent.playerSnapshot.flexibility = {
                            categoryFlexibility: 70,
                            footFlexibility: 70
                        };
                    }
                    if (!agent.playerSnapshot.favoredFoot) {
                        agent.playerSnapshot.favoredFoot = 'Either'; // Default to both feet
                    }
                    if (!agent.playerSnapshot.favoredPositionCategory) {
                        agent.playerSnapshot.favoredPositionCategory = 'Any'; // Default to flexible positioning
                    }
                    if (!agent.playerSnapshot.attributes || typeof agent.playerSnapshot.attributes !== 'object') {
                        agent.playerSnapshot.attributes = {}; // Initialize empty attributes if missing
                    }
                }
            });
        }

        return state;
    },
    '13.17.5': (state: any): any => {
        console.log('Running migration for version 13.17.5: Fixing free agent deadline logic (deadline set on first bid, not on release)');

        // Update free agent deadline logic:
        // - If totalBids === 0: remove bidDeadline (no deadline until first bid)
        // - If totalBids > 0: keep bidDeadline as-is
        if (state.freeAgents && Array.isArray(state.freeAgents)) {
            state.freeAgents = state.freeAgents.map((agent: any) => {
                // If agent has no bids, clear the deadline
                if (agent.totalBids === 0) {
                    agent.bidDeadline = undefined;
                    agent.firstBidReceivedAt = undefined;
                }
                return agent;
            });
        }

        return state;
    },
    '13.18.0': (state: any): any => {
        console.log('Running migration for version 13.18.0: Adding transfer cooldown system with per-player tracking');

        // Initialize lastTransferredAt field for all players
        // This field tracks when a player was last transferred to implement the 30-week cooldown
        for (const leagueName in state.leagues) {
            for (const team of state.leagues[leagueName].teams) {
                for (const player of team.squad) {
                    // Players don't have a transfer record yet, so leave undefined
                    if (!player.lastTransferredAt) {
                        player.lastTransferredAt = undefined;
                    }
                }

                // Also initialize for youth academy
                if (team.youthAcademy && team.youthAcademy.squad) {
                    for (const player of team.youthAcademy.squad) {
                        if (!player.lastTransferredAt) {
                            player.lastTransferredAt = undefined;
                        }
                    }
                }
            }
        }

        // Initialize for retired players
        if (state.retiredPlayers && Array.isArray(state.retiredPlayers)) {
            for (const player of state.retiredPlayers) {
                if (!player.lastTransferredAt) {
                    player.lastTransferredAt = undefined;
                }
            }
        }

        return state;
    }
};

const migrationOrder = Object.keys(migrations).sort(compareVersions);

export const migrate = (gameState: any): GameState => {
    let currentState = gameState;
    const initialStateVersion = (currentState.version || '6.9.16').split(' ')[0]; // Handle old versions with extra text

    if (compareVersions(initialStateVersion, GAME_VERSION_NUMBER) === 0) {
        return currentState as GameState;
    }
    
    if (compareVersions(initialStateVersion, GAME_VERSION_NUMBER) > 0) {
        throw new Error(`Save game version (${initialStateVersion}) is newer than game version (${GAME_VERSION_NUMBER}). Please update the game.`);
    }

    console.log(`Starting migration process from version ${initialStateVersion}`);

    for (const version of migrationOrder) {
        if (compareVersions(initialStateVersion, version) < 0) {
            const migrationFn = migrations[version];
            if (migrationFn) {
                console.log(`Applying migration for version ${version}...`);
                currentState = migrationFn(JSON.parse(JSON.stringify(currentState)));
            }
        }
    }

    currentState.version = GAME_VERSION_NUMBER;
    console.log(`Migration complete. Final version: ${currentState.version}`);
    return currentState as GameState;
};